export interface TransportPolicy {
  timeoutMs: number;
  maxAttempts: number;
  retryBaseMs: number;
  retryMaxMs: number;
  retryJitterMs: number;
}

export interface TransportRequestConfig extends Partial<TransportPolicy> {
  idempotencyKey?: string;
  requestId?: string;
}

export type TransportPolicyScope = "client" | "proxy";

const RETRYABLE_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504]);
const NON_RETRYABLE_STATUS_CODES = new Set([400, 401, 403, 404, 409, 410, 422]);
const SAFE_RETRY_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

const DEFAULT_POLICY: TransportPolicy = {
  timeoutMs: 15_000,
  maxAttempts: 3,
  retryBaseMs: 300,
  retryMaxMs: 5_000,
  retryJitterMs: 200,
};

function parsePositiveInt(
  value: string | undefined,
  fallback: number,
  min: number = 1
): number {
  if (!value) {
    return fallback;
  }

  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.max(parsed, min);
}

function resolveEnvNumber(
  keys: string[],
  fallback: number,
  min: number = 1
): number {
  for (const key of keys) {
    const value = process.env[key];
    if (value !== undefined) {
      return parsePositiveInt(value, fallback, min);
    }
  }
  return fallback;
}

export function normalizeHttpMethod(method: string | undefined): string {
  return (method ?? "GET").toUpperCase();
}

export function canRetryByMethod(
  method: string | undefined,
  idempotencyKey?: string
): boolean {
  const normalizedMethod = normalizeHttpMethod(method);
  if (SAFE_RETRY_METHODS.has(normalizedMethod)) {
    return true;
  }

  return Boolean(idempotencyKey);
}

export function isRetryableStatusCode(statusCode: number): boolean {
  if (NON_RETRYABLE_STATUS_CODES.has(statusCode)) {
    return false;
  }

  return RETRYABLE_STATUS_CODES.has(statusCode);
}

export function getTransportPolicy(
  scope: TransportPolicyScope,
  overrides: Partial<TransportPolicy> = {}
): TransportPolicy {
  const timeoutMs =
    overrides.timeoutMs ??
    resolveEnvNumber(
      scope === "proxy"
        ? [
            "CUSTDEV_PROXY_TIMEOUT_MS",
            "CUSTDEV_HTTP_TIMEOUT_MS",
            "NEXT_PUBLIC_CUSTDEV_HTTP_TIMEOUT_MS",
          ]
        : ["NEXT_PUBLIC_CUSTDEV_HTTP_TIMEOUT_MS", "CUSTDEV_HTTP_TIMEOUT_MS"],
      DEFAULT_POLICY.timeoutMs
    );

  const maxAttempts =
    overrides.maxAttempts ??
    resolveEnvNumber(
      scope === "proxy"
        ? [
            "CUSTDEV_PROXY_MAX_ATTEMPTS",
            "CUSTDEV_HTTP_MAX_ATTEMPTS",
            "NEXT_PUBLIC_CUSTDEV_HTTP_MAX_ATTEMPTS",
          ]
        : [
            "NEXT_PUBLIC_CUSTDEV_HTTP_MAX_ATTEMPTS",
            "CUSTDEV_HTTP_MAX_ATTEMPTS",
          ],
      DEFAULT_POLICY.maxAttempts
    );

  const retryBaseMs =
    overrides.retryBaseMs ??
    resolveEnvNumber(
      scope === "proxy"
        ? [
            "CUSTDEV_PROXY_RETRY_BASE_MS",
            "CUSTDEV_HTTP_RETRY_BASE_MS",
            "NEXT_PUBLIC_CUSTDEV_HTTP_RETRY_BASE_MS",
          ]
        : [
            "NEXT_PUBLIC_CUSTDEV_HTTP_RETRY_BASE_MS",
            "CUSTDEV_HTTP_RETRY_BASE_MS",
          ],
      DEFAULT_POLICY.retryBaseMs
    );

  const retryMaxMs =
    overrides.retryMaxMs ??
    resolveEnvNumber(
      scope === "proxy"
        ? [
            "CUSTDEV_PROXY_RETRY_MAX_MS",
            "CUSTDEV_HTTP_RETRY_MAX_MS",
            "NEXT_PUBLIC_CUSTDEV_HTTP_RETRY_MAX_MS",
          ]
        : [
            "NEXT_PUBLIC_CUSTDEV_HTTP_RETRY_MAX_MS",
            "CUSTDEV_HTTP_RETRY_MAX_MS",
          ],
      DEFAULT_POLICY.retryMaxMs
    );

  const retryJitterMs =
    overrides.retryJitterMs ??
    resolveEnvNumber(
      scope === "proxy"
        ? [
            "CUSTDEV_PROXY_RETRY_JITTER_MS",
            "CUSTDEV_HTTP_RETRY_JITTER_MS",
            "NEXT_PUBLIC_CUSTDEV_HTTP_RETRY_JITTER_MS",
          ]
        : [
            "NEXT_PUBLIC_CUSTDEV_HTTP_RETRY_JITTER_MS",
            "CUSTDEV_HTTP_RETRY_JITTER_MS",
          ],
      DEFAULT_POLICY.retryJitterMs,
      0
    );

  return {
    timeoutMs,
    maxAttempts,
    retryBaseMs,
    retryMaxMs,
    retryJitterMs,
  };
}

export function getRetryDelayMs(
  attempt: number,
  policy: Pick<TransportPolicy, "retryBaseMs" | "retryMaxMs" | "retryJitterMs">
): number {
  const exponentialDelay = Math.min(
    policy.retryMaxMs,
    policy.retryBaseMs * 2 ** Math.max(attempt - 1, 0)
  );
  const jitter =
    policy.retryJitterMs > 0
      ? Math.floor(Math.random() * (policy.retryJitterMs + 1))
      : 0;

  return exponentialDelay + jitter;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
